import React, { useState } from "react";
import { X, ShieldCheck, Send, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

export const ClaimProfileModal = ({ artist, open, onClose }) => {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    proof_url: "",
    message: "",
  });
  const [sending, setSending] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email) {
      toast.error("Nom et email requis");
      return;
    }
    setSending(true);
    try {
      await api.post(`/artists/${artist.user_id}/claim`, form);
      toast.success("Demande envoyée ! Nous vous répondrons sous 48-72h.");
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur d'envoi");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="claim-modal-backdrop"
    >
      <div
        className="w-full max-w-lg bg-[#1a1513] border border-white/10 rounded-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <ShieldCheck size={18} className="text-brand-red" />
            <h3 className="font-semibold">Réclamer ce profil</h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/5 rounded-lg" data-testid="claim-close">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-4">
          <div className="bg-brand-red/10 border border-brand-red/20 rounded-xl p-3 text-sm text-white/80">
            Vous êtes <strong className="text-brand-red">{artist?.name}</strong> ? Nous allons vérifier votre identité
            puis vous transférer l'accès complet à ce profil (les contenus YouTube actuels restent liés).
          </div>

          <Field label="Votre nom complet *">
            <input
              required
              type="text"
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              placeholder="Ex: Rakoto Andrianjara"
              className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-brand-red/50"
              data-testid="claim-name"
            />
          </Field>

          <Field label="Email de contact *">
            <input
              required
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="votre@email.com"
              className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-brand-red/50"
              data-testid="claim-email"
            />
          </Field>

          <Field label="Téléphone (optionnel)">
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="+261..."
              className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-brand-red/50"
              data-testid="claim-phone"
            />
          </Field>

          <Field label="Lien vers un compte officiel (Instagram, YouTube, site...)">
            <input
              type="url"
              value={form.proof_url}
              onChange={(e) => setForm({ ...form, proof_url: e.target.value })}
              placeholder="https://..."
              className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-brand-red/50"
              data-testid="claim-proof"
            />
          </Field>

          <Field label="Message à notre équipe (optionnel)">
            <textarea
              rows={3}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="Une preuve supplémentaire ? Un lien direct ?"
              className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-brand-red/50 resize-none"
              data-testid="claim-message"
            />
          </Field>

          <button
            type="submit"
            disabled={sending}
            className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="claim-submit"
          >
            {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            {sending ? "Envoi..." : "Envoyer ma demande"}
          </button>
        </form>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-[10px] tracking-[0.25em] uppercase text-white/60 font-bold mb-1.5">{label}</span>
    {children}
  </label>
);

export default ClaimProfileModal;
